import React, { useState, useMemo, useEffect } from 'react'
import { getBusinesses } from '../../services/supabase'

const FitmentCentresPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProvince, setSelectedProvince] = useState('')
  const [minRating, setMinRating] = useState(0)
  const [sortBy, setSortBy] = useState<'name' | 'rating' | 'province'>('name')
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [locationEnabled, setLocationEnabled] = useState(false)
  const [maxDistance, setMaxDistance] = useState(50)
  const [businesses, setBusinesses] = useState<any[]>([])

  useEffect(() => {
    const fetchBusinesses = async () => {
      const { data } = await getBusinesses()
      setBusinesses(data || [])
    }
    fetchBusinesses()
  }, [])

  const provinces = useMemo(() => {
    return [...new Set(businesses.map(b => b.province))].sort()
  }, [businesses])

  useEffect(() => {
    if (!locationEnabled) return
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.')
      setLocationEnabled(false)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        })
        setLocationError(null)
      },
      () => {
        setLocationError('Unable to retrieve your location.')
        setLocationEnabled(false)
      }
    )
  }, [locationEnabled])

  const filteredCentres = useMemo(() => {
    let centres = businesses

    // Apply search filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase()
      centres = centres.filter(centre =>
        centre.name?.toLowerCase().includes(lowerQuery) ||
        centre.city?.toLowerCase().includes(lowerQuery) ||
        centre.address?.toLowerCase().includes(lowerQuery) ||
        centre.province?.toLowerCase().includes(lowerQuery)
      )
    }
    // Apply province filter
    if (selectedProvince) {
      centres = centres.filter(centre => centre.province === selectedProvince)
    }
    // Apply rating filter
    if (minRating > 0) {
      centres = centres.filter(centre => centre.rating >= minRating)
    }
    // Sort results
    centres.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating ?? 0) - (a.rating ?? 0)
        case 'province':
          return (a.province ?? '').localeCompare(b.province ?? '')
        default:
          return (a.name ?? '').localeCompare(b.name ?? '')
      }
    })
    return centres
  }, [businesses, searchQuery, selectedProvince, minRating, sortBy])

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedProvince('')
    setMinRating(0)
    setSortBy('name')
    setMaxDistance(50)
  }

  const StarRating: React.FC<{ rating?: number }> = ({ rating }) => {
    const safeRating = rating || 0
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${star <= safeRating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-sm text-gray-600">
          {safeRating > 0 ? safeRating.toFixed(1) : 'No rating'}
        </span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tyre Fitment Centres</h1>
          <p className="text-gray-600">Find verified tyre installation services near you</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter Bar */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Location Enable */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <button
                onClick={() => setLocationEnabled((prev) => !prev)}
                className={`w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  locationEnabled ? 'bg-green-100 text-green-800 border border-green-400' : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                {locationEnabled ? 'Location Enabled' : 'Enable Location'}
              </button>
              {locationError && (
                <div className="text-xs text-red-600 mt-2">{locationError}</div>
              )}
              {locationEnabled && userLocation && (
                <div className="mt-2 text-xs text-gray-600">
                  Showing nearest fitment centres within
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={maxDistance}
                    onChange={e => setMaxDistance(Number(e.target.value))}
                    className="mx-1 px-2 py-1 w-16 border border-gray-300 rounded"
                  />km
                </div>
              )}
            </div>
            {/* Search Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={locationEnabled}
              />
            </div>
            {/* Province Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
              <select
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={locationEnabled}
              >
                <option value="">All Provinces</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
            </div>
            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Rating</label>
              <select
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={locationEnabled}
              >
                <option value={0}>Any Rating</option>
                <option value={4}>4+ Stars</option>
                <option value={4.5}>4.5+ Stars</option>
              </select>
            </div>
            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'rating' | 'province')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={locationEnabled}
              >
                <option value="name">Name</option>
                <option value="rating">Rating</option>
                <option value="province">Province</option>
              </select>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-gray-600">
              {filteredCentres.length} centre{filteredCentres.length !== 1 ? 's' : ''} found
            </p>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredCentres.map((centre) => (
            <div key={centre.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              {/* Centre Image/Header - Make clickable */}
              <div
                className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center relative cursor-pointer"
                style={{
                  backgroundImage: "url('https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSzbHl-OMtpHNaeZBw5SPT5fRM39ZWt9oep-g&s')",
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
                onClick={() => {
                  if (centre.slug) {
                    window.location.href = `/business/${centre.slug}`
                  } else {
                    console.warn('No slug available for this business')
                  }
                }}
              >
                <div className="text-center z-10 w-full">
                  <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 overflow-hidden shadow-lg border-4 border-white">
                    {centre.logo_url ? (
                      <img
                        src={centre.logo_url}
                        alt={centre.name}
                        className="object-cover w-20 h-20"
                      />
                    ) : (
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                  </div>
                  <h3 className="text-2xl font-extrabold text-white drop-shadow-lg">{centre.name}</h3>
                </div>
                <div className="absolute inset-0 bg-black bg-opacity-10 pointer-events-none" />
              </div>

              <div className="p-6">
                {/* Location */}
                <div className="mb-4">
                  <div className="flex items-start">
                    <svg className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-gray-600">
                      <div>{centre.address}</div>
                      <div>{centre.city}, {centre.province}</div>
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="mb-4">
                  <StarRating rating={centre.rating} />
                  <span className="ml-2 text-sm text-gray-600">({centre.review_count || 0} reviews)</span>
                </div>

                {/* Services */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Services</h4>
                  <div className="flex flex-wrap gap-1">
                    {(centre.services || []).slice(0, 3).map((service: string, index: number) => (
                      <span
                        key={index}
                        className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                    {(centre.services || []).length > 3 && (
                      <span className="text-xs text-gray-500">+{(centre.services || []).length - 3} more</span>
                    )}
                  </div>
                </div>

                {/* Specialties */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Specialties</h4>
                  <p className="text-sm text-gray-600">
                    {Array.isArray(centre.specialties)
                      ? centre.specialties.join(', ')
                      : (centre.specialties ?? '')}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="mb-4">
                  <button
                    onClick={() => {
                      if (centre.slug) {
                        window.location.href = `/business/${centre.slug}`
                      } else {
                        console.warn('No slug available for this business')
                      }
                    }}
                    className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                </div>

                {/* Contact Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      const phoneNumber = centre.contact_phone
                      if (phoneNumber) {
                        window.open(`tel:${phoneNumber}`, '_self')
                      } else {
                        alert('Phone number not available for this centre')
                      }
                    }}
                    className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call
                  </button>
                  <button
                    onClick={() => {
                      // Use address for Google Maps if coordinates aren't available
                      const address = centre.address && centre.city && centre.province
                        ? `${centre.address}, ${centre.city}, ${centre.province}`
                        : centre.address || `${centre.city}, ${centre.province}` || centre.name
                      
                      if (centre.coordinates?.lat && centre.coordinates?.lng) {
                        const { lat, lng } = centre.coordinates
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank')
                      } else if (address) {
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank')
                      } else {
                        alert('Location not available for this centre')
                      }
                    }}
                    className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    Map
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCentres.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No fitment centres found nearby</h3>
            <p className="text-gray-600 mb-4">
              {locationEnabled
                ? 'No fitment centres found within your selected distance. Try increasing the distance or suggest a centre below.'
                : 'Try adjusting your search criteria or filters, or enable location to find nearest centres.'}
            </p>
            <button
              onClick={clearFilters}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
            <div className="mt-8 flex flex-col items-center gap-4">
              <button
                className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
                onClick={() => window.open('mailto:support@yourdomain.com?subject=Suggest%20Fitment%20Centre', '_blank')}
              >
                Suggest a Fitment Centre
              </button>
              <a
                href="/request-admin-fitment-centre"
                className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900 transition-colors"
              >
                Request Admin to Find a Fitment Centre
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Booking/messaging modals hidden for now - Call and Map actions remain. */}
    </div>
  )
}

export default FitmentCentresPage
